<template>
  <b-container>
    <!-- TODO: Change options later -->
    <b-modal
      size="xl"
      :visible="selectProductModal !== null"
      :title="`Select meal for ${showSlotTime(selectedDateTime)}`"
      ok-title="Save"
      dialog-class="modal-fullscreen-sm-down"
      @hidden="$emit('update-modal', false)"
      @ok="confirm"
    >
      <b-row v-if="loading" align-h="center">
        <SmallSpinner :size="50" centered />
      </b-row>
      <b-list-group v-else>
        <RotatingSubscriptionProduct
          v-for="(product, index) in products"
          :key="index"
          :product="product"
          :selected="isSelected(product)"
          @product-selected="handleProductSelection"
          @update-modifiers="updateModifiers"
        />
      </b-list-group>
    </b-modal>
  </b-container>
</template>

<script>
import { debounce } from 'lodash';
import { axios } from '^/axios';
import { configuration } from '~/configuration';
import SmallSpinner from '@/common/SmallSpinner.vue';
import moment from 'moment';
import { mapGetters } from 'vuex';
import RotatingSubscriptionProduct from '@/subscriptions/rotating-subscription-partials/RotatingSubscriptionProduct.vue';

import {
  fetchSubscriptions,
  fetchRotatingSubscriptionProducts,
  customerSubscriptionProductSelection,
  getCustomerSubscriptionProductSelection,
} from './rotating-subscription-service';

export default {
  name: 'RotatingSubscriptionProducts',
  components: {
    SmallSpinner,
    RotatingSubscriptionProduct,
  },
  props: {
    rotation: {
      type: Object,
      required: true,
    },
    selectProductModal: {
      type: Boolean,
      default: null,
    },
    selectedProductsProps: {
      type: Array,
      default: [],
    },
    selectedDateTime: {
      type: String,
      default: '',
    },
  },
  data() {
    const date = moment();
    return {
      min: date.toDate(),
      max: date.add(2, 'month').toDate(),
      loading: true,

      configuration,
      products: [],
      selectedProducts: [], // Store for selected products
    };
  },
  async mounted() {
    await this.fetchProducts();
  },

  computed: {
    ...mapGetters(['vendor', 'region', 'user']),
  },

  methods: {
    async confirm(bvModalEvent) {
      bvModalEvent.preventDefault();
      const dateTime = this.selectedDateTime;
      try {
        const products = this.selectedProducts.map((product) => {
          return {
            product_uuid: product.product_uuid,
            product_id: product.product_id,
            product_quantity: product.quantityOrdered ?? 1, //TODO: Change and make it dynamic
            unit_price: product.unit_price,
            rotating_subscription_product_id: product.id,
            product_modifiers: product.product_modifiers
              .map((x) => x.items)
              .flat()
              .filter((x) => x.item_quantity > 0 || x.item_quantity < 0)
              .map((x) => ({
                item_uuid: x.uuid,
                item_quantity: x.item_quantity,
              })),
          };
        });

        const data = {
          products: products,
          subscription_time: moment(dateTime).format('HH:mm:ss'),
          subscription_day: moment(dateTime).day(),
          customer_id: this.user.id,
          rotating_subscription_id: this.rotation.id,
          scheduled: moment(this.selectedDateTime)
            .utc()
            .format('YYYY-MM-DDTHH:mm:ss[Z]'),
        };

        this.loading = true;

        // For each product, make an individual API call with the required fields
        const promises = products.map((product) => {
          const selectionData = {
            customer_id: this.user.id,
            rotating_subscription_id: this.rotation.id,
            subscription_day: moment(dateTime).day(),
            subscription_time: moment(dateTime).format('HH:mm:ss'),
            rotating_subscription_product_id:
              product.rotating_subscription_product_id,
            quantity: product.product_quantity || 1,
          };

          // Add modifiers if available
          if (
            product.product_modifiers &&
            product.product_modifiers.length > 0
          ) {
            selectionData.modifiers = product.product_modifiers.map(
              (modifier) => ({
                modifier_item_id: modifier.id,
                quantity: modifier.item_quantity,
              })
            );
          }

          return customerSubscriptionProductSelection({
            data: selectionData,
          });
        });

        await Promise.all(promises);
        return true;
      } catch (e) {
        console.error('Error saving selections:', e);
        console.error('Error response:', e.response?.data);
        await this.$store.dispatch('alertError', ['danger', e.message]);
      } finally {
        this.loading = false;
        this.$emit('update-modal', false, 'success');
      }
    },
    isSelected(product) {
      return this.selectedProductsProps.some(
        (selectedProduct) => selectedProduct.id === product.id
      );
    },
    handleProductSelection({ product, isSelected }) {
      if (isSelected) {
        this.selectedProducts.push(product);
      } else {
        this.selectedProducts = this.selectedProducts.filter(
          (p) => p.id !== product.id
        );
      }
    },
    async fetchProducts() {
      if (this.products.length > 0) {
        return;
      }
      try {
        this.loading = true;
        const { data } = await fetchRotatingSubscriptionProducts({
          rotationUuid: this.rotation.uuid,
        });

        let products = data;

        // await this.getCustomerSubscriptionProductSelection(this.selectedDateTime);

        this.products = products;
      } catch (e) {
        await this.$store.dispatch('alertError', ['danger', e.message]);
      } finally {
        this.loading = false;
      }
    },

    async getCustomerSubscriptionProductSelection(scheduled) {
      let products = [];
      scheduled = moment(scheduled).utc().format('YYYY-MM-DDTHH:mm:ss[Z]');
      try {
        this.loading = true;
        const response = await getCustomerSubscriptionProductSelection(
          scheduled
        );
        products = response.products;
      } catch (e) {
        await this.$store.dispatch('alertError', ['danger', e.message]);
      } finally {
        this.loading = false;

        this.selectedProducts = products;
      }
    },

    updateModifiers({ product, modifiers }) {
      // Connect modifiers to product
      const targetProduct = this.products.find(
        (p) => p.product_uuid === product.product_uuid
      );

      if (targetProduct) {
        this.$set(targetProduct, 'product_modifiers', modifiers);
      }
    },

    showSlotTime(slotDate) {
      return moment(slotDate).tz(this.region.timezone).format('MMMM Do h:mm a');
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../../../styles/variables.module';
@media #{$belowMd} {
  ::v-deep .modal-dialog {
    margin: 0 auto;
    height: 100%;
    min-height: -webkit-fill-available;
    max-height: -webkit-fill-available;
    .modal-content {
      max-height: unset;
    }
  }
}
</style>
